import React, { useState } from 'react'
import { Form, Button } from 'react-bootstrap'
import { requiredFieldSign } from '../ui'
import Select from 'react-select'
import { POSTBACK_STATUS_OPTIONS, POSTBACK_CURRENCY_OPTIONS } from '../../constants'
import { reactSelectStyles } from '../filters'
import { ISelectOption } from '../../types/ui'
import { EPostbackStatus, EPostbackCurrency } from '../../types'
import { useAtomValue, useSetAtom } from 'jotai'
import * as state from '../../states'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import CreatableSelect from 'react-select/creatable'
import * as lib from '../../lib'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

const GridBoxes = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin: 30px 0;
    //TODO: check the media point
    @media all and (max-width: 576px) {
        grid-template-columns: repeat(1, 1fr);
    }
`

const GridBox = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
`

const ButtonBox = styled.div`
    display: flex;
    justify-content: center;

    button.btn {
        width: 300px;
    }
`

type IInitState = {
	subId: ISelectOption<string>[]
	postbackStatus: EPostbackStatus | null
	postbackCurrency: EPostbackCurrency | null
	postbackAmount: string
}

export const PostbackSendForm = () => {
	const { t } = useTranslation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const POSTBACK_STATUS_OPTIONS_TRANS = POSTBACK_STATUS_OPTIONS(t)
	const POSTBACK_CURRENCY_OPTIONS_TRANS = POSTBACK_CURRENCY_OPTIONS(t)

	const initState: IInitState = {
		subId: [],
		postbackStatus: POSTBACK_STATUS_OPTIONS_TRANS?.[0]?.value,
		postbackCurrency: POSTBACK_CURRENCY_OPTIONS_TRANS?.[0]?.value,
		postbackAmount: '0'
	}

	const [subId, setSubId] = useState<IInitState['subId']>(initState.subId)
	const [postbackStatus, setPostbackStatus] = useState<IInitState['postbackStatus']>(initState.postbackStatus)
	const [postbackCurrency, setPostbackCurrency] = useState<IInitState['postbackCurrency']>(initState.postbackCurrency)
	const [postbackAmount, setPostbackAmount] = useState<IInitState['postbackAmount']>(initState.postbackAmount)

	const resetFormState = () => {
		setSubId(initState.subId)
		setPostbackStatus(initState.postbackStatus)
		setPostbackCurrency(initState.postbackCurrency)
		setPostbackAmount(initState.postbackAmount)
	}

	const isSendBtnActive =
		subId.length > 0 &&
		!!postbackStatus &&
		!!postbackCurrency &&
		postbackAmount.length > 0 && +postbackAmount >= 0

	const onSend = async () => {
		if (!isSendBtnActive) return

		const uri: string = `postback/send`
		const method: string = 'POST'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)

		const subIdArr = subId.map(item => item.value)
		const body: string = JSON.stringify({
			subid: subIdArr,
			status: postbackStatus,
			payout: +postbackAmount,
			currency: postbackCurrency
		})
		const options: lib.FetchOptions = { body, method, params, headers }

		try {
			setFetchIsLoading(state.turnOnLoader)
			const result = await lib.fetching<void>(uri, options)
			//console.log('Postback Send response:', { result })
			resetFormState()
			toast.success(
				`${t(ALL_LANG_KEY.postback_send_form.msg_postback_send_success)}`,
				{
					autoClose: 5000,
					hideProgressBar: false
				}
			)
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
			toast.error(
				`${t(ALL_LANG_KEY.postback_send_form.msg_postback_send_error)}`,
				{
					autoClose: 5000,
					hideProgressBar: false
				}
			)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	return (
		<div>
			<Form>
				<Form.Group>
					<Form.Label className="text-dark" htmlFor={`select-sub-id`}>
						{`${t(ALL_LANG_KEY.postback_send_form.field_sub_id_label)}`}
						{requiredFieldSign}
					</Form.Label>
					<CreatableSelect
						inputId={`select-sub-id`}
						isMulti={true} // Enables multi-selection
						options={[]}
						value={subId}
						placeholder={`${t(ALL_LANG_KEY.postback_send_form.field_sub_id_placeholder)}`}
						noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
						formatCreateLabel={(inputValue) => `${t(ALL_LANG_KEY.general.react_select_create_label)}: ${inputValue}`}
						styles={reactSelectStyles<ISelectOption<string>, true>(isThemeDark)}
						onChange={(newValue) => {
							//console.log('CreatableSelect onChange:', { newValue })
							setSubId([...newValue])
						}}
						onCreateOption={(inputValue) => {
							const inputValueArr = inputValue.split(/\s+/)
							const prevOptionsValues = subId.map(item => item.value)
							const newOptionsValues = Array.from(new Set([...prevOptionsValues, ...inputValueArr]))

							const newOptions = newOptionsValues.map(
								(item) => ({
									value: item,
									label: item
								})
							)
							//console.log('onCreateOption:: ', { inputValue, inputValueArr, prevOptionsValues, newOptionsValues })
							setSubId(newOptions)
						}}
					/>

					<GridBoxes>
						<GridBox>
							<Form.Label className="text-dark" htmlFor={'select-postback-status'}>
								{`${t(ALL_LANG_KEY.postback_send_form.field_postback_status_label)}`}
								{requiredFieldSign}
							</Form.Label>
							<Select
								inputId={'select-postback-status'}
								className="react-select-container"
								options={POSTBACK_STATUS_OPTIONS_TRANS}
								value={POSTBACK_STATUS_OPTIONS_TRANS.find(item => item.value === postbackStatus) ?? null}
								placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
								noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
								isSearchable={false}
								styles={reactSelectStyles<ISelectOption<EPostbackStatus>, false>(isThemeDark)}
								onChange={(selectedItem) => {
									setPostbackStatus(selectedItem?.value ?? null)
								}}
							/>
						</GridBox>

						<GridBox>
							<Form.Label className="text-dark" htmlFor={'select-postback-currency'}>
								{`${t(ALL_LANG_KEY.postback_send_form.field_postback_currency_label)}`}
								{requiredFieldSign}
							</Form.Label>
							<Select
								inputId={'select-postback-currency'}
								className="react-select-container"
								options={POSTBACK_CURRENCY_OPTIONS_TRANS}
								value={POSTBACK_CURRENCY_OPTIONS_TRANS.find(item => item.value === postbackCurrency) ?? null}
								placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
								noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
								isSearchable={false}
								styles={reactSelectStyles<ISelectOption<EPostbackCurrency>, false>(isThemeDark)}
								onChange={(selectedItem) => {
									setPostbackCurrency(selectedItem?.value ?? null)
								}}
							/>
						</GridBox>

						<GridBox>
							<Form.Label className="text-dark" htmlFor="input-postback-amount">
								{`${t(ALL_LANG_KEY.postback_send_form.field_postback_amount_label)}`}
								{requiredFieldSign}
							</Form.Label>
							<Form.Control
								id="input-postback-amount"
								placeholder={`${t(ALL_LANG_KEY.postback_send_form.field_postback_amount_placeholder)}`}
								type="number"
								step={0.00001}
								value={postbackAmount}
								disabled={false}
								onChange={(e) => setPostbackAmount(e.target.value)}
							/>
						</GridBox>
					</GridBoxes>

				</Form.Group>
			</Form>

			<ButtonBox>
				<Button variant="primary"
				        onClick={onSend}
				        disabled={!isSendBtnActive}
				>
					{`${t(ALL_LANG_KEY.postback_send_form.btn_send)}`}
				</Button>
			</ButtonBox>
		</div>
	)
}